// src/utils/billingTiers.js

// ─── Plan Definitions ─────────────────────────────────────────────────────────
export const PLANS = {
  free:    { label: 'Free',    price: 0,    color: '#6b7280' },
  starter: { label: 'Starter', price: 100,  color: '#3b82f6' },
  pro:     { label: 'Pro',     price: 350,  color: '#8b5cf6' },
  elite:   { label: 'Elite',   price: 500,  color: '#f59e0b' },
  premium: { label: 'Premium', price: 1000, color: '#10b981' },
};

// Lowest → highest tier, used for upgrade-comparison logic
export const PLAN_ORDER = ['free', 'starter', 'pro', 'elite', 'premium'];

// Legacy aliases kept so existing payment/billing code doesn't break
export const VENDOR_SETUP_FEE      = 100;
export const VENDOR_PLUS_SETUP_FEE = 200;
export const VENDOR_PLUS_ADDON_FEE = 100;
export const VENDOR_MONTHLY        = 0;
export const VENDOR_PLUS_MONTHLY   = 0;
export const SHOP_OPENING_FEE      = VENDOR_SETUP_FEE;

// ─── Plan Permissions ─────────────────────────────────────────────────────────
// maxVisible — max combined visible product + service cards (one shared pool;
//              gig cards under the "need" tab are unlimited on every plan)
// maxTabs — max navigation tabs a shop can have visible at once (the 3 default
//           tabs — Home, Shop, Profile — count against this same limit)
// canPostGigs — can post gig cards (jobs needed / contracts tab)
// canPickupGigs — can accept/respond to someone else's gig card
// boostedListings — listings get priority placement in nearby/search results
export const PLAN_PERMISSIONS = {
  free:    { maxVisible: 1,  maxTabs: 1,  canPostGigs: true, canPickupGigs: false, boostedListings: false },
  starter: { maxVisible: 3,  maxTabs: 2,  canPostGigs: true, canPickupGigs: false, boostedListings: false },
  pro:     { maxVisible: 15, maxTabs: 3,  canPostGigs: true, canPickupGigs: true,  boostedListings: false },
  elite:   { maxVisible: 25, maxTabs: 5,  canPostGigs: true, canPickupGigs: true,  boostedListings: false },
  premium: { maxVisible: 50, maxTabs: 10, canPostGigs: true, canPickupGigs: true,  boostedListings: true  },
};

// ─── Plan Helpers ─────────────────────────────────────────────────────────────
export function getPlanFromBilling(billing) {
  if (!billing) return 'free';
  if (billing.plan) return billing.plan;
  // Legacy: if opening fee was paid, treat as starter
  if (billing.openingFeePaid === true) return 'starter';
  return 'free';
}

export function getPlanPermissions(billing) {
  const plan = getPlanFromBilling(billing);
  return PLAN_PERMISSIONS[plan] || PLAN_PERMISSIONS.free;
}

// Count how many products/services are currently visible in a shop's item list.
// visibleTotal is the number that counts against a plan's shared maxVisible pool.
export function countVisibleByType(items = [], services = []) {
  const visibleProducts = items.filter(i => i.visible && !i.isService && !i.jobType).length;
  const visibleServices = services.filter(s => s.visible && s.isService && (s.serviceTab || 'offer') === 'offer').length;
  return { visibleProducts, visibleServices, visibleTotal: visibleProducts + visibleServices };
}

// Can a specific item be made visible given current plan + current combined count?
export function canMakeVisible(billing, itemType, currentCount) {
  const perms = getPlanPermissions(billing);
  if (itemType === 'product' || itemType === 'service') return currentCount < perms.maxVisible;
  if (itemType === 'gig')     return perms.canPostGigs;
  return false;
}

// Backward-compat helper used in ShopPage for item visibility defaults
export function canItemTypeBeVisible(billing, item) {
  const perms = getPlanPermissions(billing);
  if (!item) return false;
  // Need-tab service cards (gigs / jobs the seller is looking to fill) are free on every plan
  if (item.isService && (item.serviceTab || 'offer') === 'need') return perms.canPostGigs;
  // Offer-tab service cards and regular products share the same maxVisible pool
  return perms.maxVisible > 0;
}

// Can another tab be made visible (or added) given current plan + current visible tab count?
export function canAddOrShowTab(billing, currentVisibleTabCount) {
  const perms = getPlanPermissions(billing);
  return currentVisibleTabCount < perms.maxTabs;
}

// Human-readable perk bullets for a plan, generated from PLAN_PERMISSIONS so
// upgrade UIs (PaymentModal, LiveShopCreation pricing section) never drift
// out of sync with the actual enforced limits.
export function getPlanPerks(planKey) {
  const perms = PLAN_PERMISSIONS[planKey] || PLAN_PERMISSIONS.free;
  const perks = [
    `📦 ${perms.maxVisible} visible product/service card${perms.maxVisible !== 1 ? 's' : ''}`,
    `🗂️ ${perms.maxTabs} tab${perms.maxTabs !== 1 ? 's' : ''}`,
    perms.canPickupGigs ? '✅ Pick up gigs' : '🚫 Cannot pick up gigs',
  ];
  if (perms.boostedListings) perks.push('🚀 Boosted listings');
  return perks;
}

// ─── Outstanding Balance ───────────────────────────────────────────────────────
export function isMonthlyDue(_billing) {
  // Plans are one-time fees — no recurring monthly charge
  return false;
}

export function getMonthlyAmount(_billing) {
  return 0;
}

export function calculateOutstandingBalance(billingData) {
  if (!billingData) return 0;
  if (billingData.plan) return 0; // one-time fee already paid at signup
  // Legacy milestone system
  const totalSales     = billingData.totalSales || 0;
  const totalPaidFees  = (billingData.paymentHistory?.paidMilestoneFees || []).reduce((s, f) => s + f, 0);
  const currentMilestone = getLegacyCurrentMilestone(totalSales);
  const shouldHavePaid   = currentMilestone ? currentMilestone.cumulativeTotal : 0;
  return Math.max(0, SHOP_OPENING_FEE + shouldHavePaid - totalPaidFees);
}

export function shouldHideItems(billingData) {
  if (!billingData) return false;
  return billingData.itemsHiddenDueToBalance === true;
}

// ─── Payment Breakdown ────────────────────────────────────────────────────────
export function getPaymentBreakdown(billingData) {
  if (!billingData) {
    return {
      plan: 'free', totalSales: 0, amountOwed: 0,
      itemsHidden: false, isMonthlyDue: false, monthlyAmount: 0,
      totalPaidFees: 0, currentMilestone: null, nextMilestone: null, salesUntilNext: 0,
    };
  }
  const plan      = getPlanFromBilling(billingData);
  const amountOwed = calculateOutstandingBalance(billingData);
  return {
    plan,
    planLabel:       PLANS[plan]?.label || 'Free',
    totalSales:      billingData.totalSales || 0,
    amountOwed,
    itemsHidden:     billingData.itemsHiddenDueToBalance === true,
    isMonthlyDue:    false,
    monthlyAmount:   0,
    nextBillingDate: null,
    totalPaidFees:   (billingData.paymentHistory?.paidMilestoneFees || []).reduce((s, f) => s + f, 0),
    currentMilestone: billingData.plan ? null : getLegacyCurrentMilestone(billingData.totalSales || 0),
    nextMilestone:    billingData.plan ? null : getLegacyNextMilestone(billingData.totalSales || 0),
    salesUntilNext:   0,
  };
}

// ─── Legacy Milestone Helpers (existing users without billing.plan) ────────────
const LEGACY_MILESTONES = [
  { threshold: 100,  fee: 20,  cumulativeTotal: 20  },
  { threshold: 300,  fee: 30,  cumulativeTotal: 50  },
  { threshold: 500,  fee: 50,  cumulativeTotal: 100 },
  { threshold: 1000, fee: 100, cumulativeTotal: 200 },
  { threshold: 1500, fee: 50,  cumulativeTotal: 250 },
  { threshold: 2000, fee: 100, cumulativeTotal: 350 },
  { threshold: 2500, fee: 50,  cumulativeTotal: 400 },
  { threshold: 3000, fee: 100, cumulativeTotal: 500 },
  { threshold: 3500, fee: 50,  cumulativeTotal: 550 },
  { threshold: 4000, fee: 100, cumulativeTotal: 650 },
];

function getLegacyCurrentMilestone(totalSales) {
  if (totalSales === 0) return null;
  for (let i = 0; i < LEGACY_MILESTONES.length; i++) {
    if (totalSales < LEGACY_MILESTONES[i].threshold) return i > 0 ? LEGACY_MILESTONES[i - 1] : null;
  }
  return LEGACY_MILESTONES[LEGACY_MILESTONES.length - 1];
}

function getLegacyNextMilestone(totalSales) {
  for (const m of LEGACY_MILESTONES) {
    if (totalSales < m.threshold) return m;
  }
  const last  = LEGACY_MILESTONES[LEGACY_MILESTONES.length - 1];
  const excess = totalSales - last.threshold;
  const next   = Math.ceil(excess / 500);
  return { threshold: last.threshold + next * 500, fee: next % 2 === 0 ? 100 : 50 };
}

export const SALES_MILESTONES   = LEGACY_MILESTONES;
export const getCurrentMilestone = getLegacyCurrentMilestone;
export const getNextMilestone    = getLegacyNextMilestone;

export function generateMilestone(salesAmount) { return getLegacyNextMilestone(salesAmount); }

export function getMilestoneWarning(totalSales) {
  const next = getLegacyNextMilestone(totalSales);
  if (!next) return null;
  const salesUntilNext = next.threshold - totalSales;
  if (salesUntilNext <= 50)  return { type: 'urgent',  message: `$${salesUntilNext.toFixed(2)} until next fee` };
  if (salesUntilNext <= 100) return { type: 'warning', message: `Approaching $${next.threshold} milestone` };
  return null;
}

// ─── Utilities ────────────────────────────────────────────────────────────────
export function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
}

export function validatePromoCode(code, promoCodes) {
  if (!code || !promoCodes || promoCodes.length === 0) return null;
  return promoCodes.find(p =>
    p.code.trim().toLowerCase() === code.trim().toLowerCase() && p.active !== false
  ) || null;
}

export function isPaymentOverdue(billingData) {
  return calculateOutstandingBalance(billingData) > 0;
}

export function getDeadlineMessage(billingData) {
  if (!billingData) return null;
  if (billingData.plan) return null; // one-time plans — no deadline
  return null;
}
